import { defineStore } from "pinia";
import { computed, ref } from "vue";
import ApiDataSources from "@/apis/ApiDataSources";
import { catchable } from "./piniaUtils";
import { useToastsStore } from "./toastsStore";
import { useCache } from "@/utils/composables/useCache";

export const useDataSourcesStore = defineStore("data-sources", () => {
  const dataSources = ref([]);
  const { cacheable } = useCache();
  const toastsStore = useToastsStore();

  const mutateUpdateDataSource = dataSource => {
    dataSources.value = dataSources.value.map(item =>
      item && item.id === dataSource.id ? dataSource : item,
    );
  };

  const dataSourcesToObject = computed(() => {
    if (!dataSources.value?.length) return {};
    const obj = {};
    dataSources.value.forEach(ds => {
      const nestedObj = {};
      ds.data_source_items?.forEach(item => {
        if (item && item.key) nestedObj[item.key] = item.data;
      });
      if (ds && ds.name) obj[ds.name] = nestedObj;
    });
    return obj;
  });

  const getDataSources = (appId, forceUpdate = false) =>
    catchable({
      t: async () =>
        (dataSources.value =
          (await cacheable(() => ApiDataSources.getDataSources(appId), appId, forceUpdate)) || []),
      loadable: true,
    });

  const createDataSource = ({ appId, model }) =>
    catchable({
      t: async () =>
        (dataSources.value = [
          ...dataSources.value,
          await ApiDataSources.createDataSource(appId, model),
        ]),
      loadable: true,
    });

  const upsertDataSourceCSV = ({ appId, dataSourceId, csv }) =>
    catchable({
      t: async () => {
        mutateUpdateDataSource(await ApiDataSources.upsertFromCSV(appId, dataSourceId, csv));
        toastsStore.create({ type: "success", body: "Successfully uploaded CSV" });
      },
      loadable: true,
    });

  const editDataSource = ({ appId, model }) =>
    catchable({
      t: async () => {
        mutateUpdateDataSource(await ApiDataSources.editDataSource(appId, model));
        toastsStore.create({ type: "success", body: "Successfully updated data source" });
      },
      loadable: true,
    });

  const deleteDataSource = ({ appId, dataSourceId }) =>
    catchable({
      t: async () => {
        await ApiDataSources.deleteDataSource(appId, dataSourceId);
        dataSources.value = dataSources.value.filter(ds => ds && ds.id !== dataSourceId);
      },
      loadable: true,
    });

  return {
    dataSources,
    getDataSources,
    createDataSource,
    upsertDataSourceCSV,
    editDataSource,
    deleteDataSource,
    dataSourcesToObject,
  };
});
